import Icon from '@ant-design/icons';
import { Button, FormInstance, Image, message, Progress, Rate, Select, Statistic, Upload } from 'antd';
import React, { Component } from 'react';
import { FormattedDate, FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { Link, RouteComponentProps } from 'react-router-dom';
import mediaApi from '../../apis/MediaApi';
import positionApi from '../../apis/PositionApi';
import recordApi from '../../apis/RecordApi';
import resultApi from '../../apis/ResultApi';
import seasonApi from '../../apis/SeasonApi';
import sportApi from '../../apis/SportApi';
import userRatingApi from '../../apis/UserRatingApi';
import LayoutComponent from '../../components/LayoutComponent/LayoutComponent';
import CustomContext from '../../context/CustomContext';
import {
    Media,
    Position,
    Record,
    Result,
    Season,
    Sport,
    Station,
    TestCategory,
    User,
    UserOrganizationMember,
    UserRating,
    UserSport,
} from '../../model/Entities';
import { ReactComponent as addSvg } from '../../resources/images/ico-add.svg';
import { ReactComponent as editSvg } from '../../resources/images/ico-edit.svg';
import { ReactComponent as nextSvg } from '../../resources/images/ico-next.svg';
import { ReactComponent as playSvg } from '../../resources/images/ico-play.svg';
import { ReactComponent as prevSvg } from '../../resources/images/ico-prev.svg';
import { ReactComponent as shareSvg } from '../../resources/images/ico-share.svg';
import { ReactComponent as starSvg } from '../../resources/images/ico-star.svg';
import HeadMetadata from '../../services/HeadMetadata';
import metricService from '../../services/MetricService';
import notificationService from '../../services/NotificationService';
import numericService from '../../services/NumericService';
import playerService from '../../services/PlayerService';
import MediaPlayerComponent from '../PlayerMediaPage/MediaPlayerComponent/MediaPlayerComponent';
import PlayerShareModal from '../PlayerProfilePage/PlayerShareModal/PlayerShareModal';
import AddMetricModal from './AddMetricModal/AddMetricModal';
import styles from './PlayerDataPage.module.scss';
import avatar from '../../resources/images/profile-placeholder.png';
import PlayerGameStatisticsComponent from './PlayerGameStatisticsComponent/PlayerGameStatisticsComponent';
import prospectsApi from '../../apis/ProspectsApi';
import userSportApi from '../../apis/UserSportApi';

class PlayerDataPage extends Component<Props, State> {
    static contextType = CustomContext;
    context!: React.ContextType<typeof CustomContext>;
    formRef = React.createRef<FormInstance>();

    constructor(props: Props) {
        super(props);
        this.state = {
            positions: [],
            seasons: [],
            userSports: [],
            sports: [],
            userOrganizationsBySport: [],
        };
    }

    componentDidMount() {
        this.init();
    }

    /** METHODS **/

    init = async () => {
        try {
            this.setState({ loading: 'initializing' });

            // get seasons
            const params = new URLSearchParams(this.props.location.search);
            const sportId = params.get('sportId') ? +params.get('sportId')! : 2;
            const seasons = await seasonApi.listAll(sportId);
            const seasonId = seasons.find((s) => s.isActive === true)?.id!;

            // get player
            const responses = await Promise.all([
                playerService.getPlayer(this.props),
                recordApi.list(this.props.match.params.id, sportId, seasonId),
                resultApi.list(this.props.match.params.id, sportId, seasonId),
                positionApi.list(sportId),
                sportApi.listAll(),
                userRatingApi.list(this.props.match.params.id, sportId, seasonId),
            ]);
            const player = responses[0];
            const records = responses[1].filter(
                (r) => player.sport.stations?.flatMap((s) => s.metrics?.filter((m) => m.id === r.metricId))[0]?.name,
            );
            const results = responses[2].filter(
                (r) => player.sport.stations?.flatMap((s) => s.metrics?.filter((m) => m.id === r.metricId))[0]?.name,
            );
            const testCategory =
                results.length > 0
                    ? player.sport.stations?.flatMap((s) => s.metrics?.filter((m) => m.id === results[0].metricId))[0]
                    : undefined;
            const stations = player.sport.stations;
            const positions = responses[3];
            const sports = responses[4];
            const userRatings = responses[5];
            const userRating = userRatings.find((u) => u.sportId === player.sport.id && u.seasonId === seasonId);
            const playerData = playerService.getPlayerData(player.user, player.sport);

            // get permissions
            const editable = playerService.isEditable(player.user, this.context.auth, seasons, seasonId);

            this.setState({
                ...player,
                records,
                results,
                testCategory,
                stations,
                positions,
                seasons,
                seasonId,
                sports,
                userRating,
                ...playerData,
                editable,
            });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    refresh = async (newSeasonId?: number) => {
        const { user, sport, seasonId, testCategory, seasons } = this.state;
        try {
            this.setState({ loading: 'loading' });
            const seasonIdToUse = newSeasonId ? newSeasonId : seasonId;
            const responses = await Promise.all([
                recordApi.list(this.props.match.params.id, sport?.id!, seasonIdToUse),
                resultApi.list(this.props.match.params.id, sport?.id!, seasonIdToUse),
                userRatingApi.list(this.props.match.params.id, sport?.id!, seasonIdToUse!),
            ]);
            const records = responses[0].filter(
                (r) => sport?.stations?.flatMap((s) => s.metrics?.filter((m) => m.id === r.metricId))[0]?.name,
            );
            const results = responses[1].filter(
                (r) => sport?.stations?.flatMap((s) => s.metrics?.filter((m) => m.id === r.metricId))[0]?.name,
            );

            const userRatings = responses[2];
            const userRating = userRatings.find((u) => u.sportId === sport?.id && u.seasonId === seasonIdToUse);

            const newTestCategory = newSeasonId
                ? results.length > 0
                    ? sport?.stations?.flatMap((s) => s.metrics?.filter((m) => m.id === results[0].metricId))[0]
                    : undefined
                : testCategory;

            // get permissions
            const editable = playerService.isEditable(user!, this.context.auth, seasons, seasonIdToUse);

            this.setState({
                records,
                results,
                seasonId: seasonIdToUse,
                testCategory: newTestCategory,
                userRating,
                editable,
            });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    showModalShare = (modalShareVisible: boolean) => {
        this.setState({ modalShareVisible });
    };

    showModalMeasure = (modalMeasureVisible: boolean, metricId?: number, measureId?: number) => {
        this.setState({ modalMeasureVisible, metricId, measureId });
    };

    showModalPlayer = (modalPlayerVisible: boolean) => {
        this.setState({ modalPlayerVisible });
    };

    update = async () => {
        this.showModalMeasure(false);
        await this.refresh();
    };

    cancel = () => {
        const metricId = undefined;
        this.showModalMeasure(false);
        this.setState({ metricId });
    };

    setCategory = (testCategory?: TestCategory) => {
        this.setState({ testCategory });
    };

    uploadVideo = async (file: File, resultId: number) => {
        const { user } = this.state;
        try {
            if (!/\.(mp4|mpe?g|mpg|mpv|mp2|ogg|m4p|m4v|avi|wmv|mov|qt)$/i.test(file.name)) {
                message.error(`${file.name} ${this.props.intl.formatMessage({ id: 'status.videoFormat' })}`, 6);
                return false;
            } else {
                this.getBase64(file, async (video: string) => {
                    try {
                        this.setState({ loading: 'uploading' });
                        await mediaApi.uploadVideo(user?.id!, resultId, file);
                        await this.refresh();
                    } catch (error) {
                        notificationService.displayError(error, this.props.intl);
                    } finally {
                        this.setState({ loading: undefined });
                    }
                });
                return false;
            }
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        }
    };

    getBase64 = (img: any, callback?: any) => {
        const reader = new FileReader();
        reader.addEventListener('load', () => callback(reader.result));
        reader.readAsDataURL(img);
    };

    playMedia = (medias: Media[]) => {
        this.showModalPlayer(true);
        this.setState({ medias });
    };

    getBestResult = (results: Result[], testCategory: TestCategory): Result | undefined => {
        let topResult: Result | undefined;
        const resultsByCategory = results.filter((r) => r.metricId === testCategory.id);
        if (resultsByCategory.length > 0) {
            const ascending = testCategory.best === 'max';
            if (ascending) {
                topResult = resultsByCategory.reduce((prev, current) =>
                    prev.result! > current.result! ? prev : current,
                );
            } else {
                topResult = resultsByCategory.reduce((prev, current) =>
                    prev.result! < current.result! ? prev : current,
                );
            }
        }

        return topResult;
    };

    getMaxResult = (results: Result[], testCategory: TestCategory): Result | undefined => {
        let maxResult: Result | undefined;
        const resultsByCategory = results.filter((r) => r.metricId === testCategory.id);
        if (resultsByCategory.length > 0) {
            maxResult = resultsByCategory.reduce((prev, current) => (prev.result! > current.result! ? prev : current));
        }

        return maxResult;
    };

    changeUserSport = async (userSportId: number) => {
        const { user, userSports } = this.state;
        try {
            this.setState({ loading: 'loading' });

            const userSport = userSports.find((u) => u.id === userSportId);
            const sport = await sportApi.get(userSport?.sportId);
            const seasons = await seasonApi.listAll(sport?.id!);
            const seasonId = seasons && seasons.length > 0 ? seasons[0].id : undefined;
            const responses = await Promise.all([
                positionApi.list(sport?.id!),
                recordApi.list(this.props.match.params.id, sport?.id!, seasonId),
                resultApi.list(this.props.match.params.id, sport?.id!, seasonId),
                userRatingApi.list(this.props.match.params.id, sport?.id!, seasonId!),
            ]);
            const positions = responses[0];
            const records = responses[1].filter(
                (r) => sport?.stations?.flatMap((s) => s.metrics?.filter((m) => m.id === r.metricId))[0]?.name,
            );
            const results = responses[2].filter(
                (r) => sport?.stations?.flatMap((s) => s.metrics?.filter((m) => m.id === r.metricId))[0]?.name,
            );
            const testCategory =
                results.length > 0
                    ? sport?.stations?.flatMap((s) => s.metrics?.filter((m) => m.id === results[0].metricId))[0]
                    : undefined;

            const stations = sport?.stations;

            const userRatings = responses[3];
            const userRating = userRatings.find((u) => u.sportId === sport?.id && u.seasonId === seasonId);

            const playerData = playerService.getPlayerData(user!, sport);

            // get permissions
            const editable = playerService.isEditable(user!, this.context.auth, seasons, seasonId);

            // change page
            this.props.history.push(`/players/${user!.id}/data?sportId=${userSport?.sportId}`);

            this.setState({
                userSport,
                sport,
                positions,
                seasons,
                records,
                results,
                testCategory,
                stations,
                seasonId,
                userRating,
                userOrganizationsBySport: playerData.userOrganizationsBySport,
                organization: playerData.organization,
                editable,
            });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    changeAffiliate = (organization: string) => {
        this.setState({ organization });
    };

    rate = async (rate: number) => {
        const { user, userSport } = this.state;
        try {
            if (rate > 0) {
                if (userSport?.rate === undefined && userSport?.note === undefined) {
                    await prospectsApi.create(user?.id!, userSport?.sportId!, rate);
                } else {
                    await prospectsApi.update(user?.id!, userSport?.sportId!, rate, userSport.note);
                }

                const userSports = await userSportApi.list(user?.id!);
                const userSportAux = userSports.find((us) => us.sportId === userSport?.sportId);

                this.setState({ userSports, userSport: userSportAux });
            }
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        }
    };

    /*** COMPONENTS ***/

    renderHeader = (desktop: boolean): React.ReactElement | undefined => {
        const { sport, user, userSports, userSport, sports, seasons, seasonId } = this.state;

        const seasonOptions = seasons.map((season) => (
            <Select.Option key={season.id!} value={season.id!}>
                {season.name}
            </Select.Option>
        ));

        const userSportsOptions = userSports.map((us) => (
            <Select.Option key={us.id!} value={us.id!}>
                {sports?.find((s) => s.id === us.sportId)?.name}
            </Select.Option>
        ));

        if (desktop) {
            return (
                <div className={styles.header}>
                    <div className={styles.headerBackground}>
                        <div className={styles.headerBackgroundGradient}></div>
                        <div
                            className={styles.headerBackgroundImage}
                            style={{ backgroundImage: `url(${sport?.backgroundUrl})` }}
                        ></div>
                    </div>

                    <div className={styles.buttons}>
                        {this.context.auth?.authorities.includes('ROLE_COLLEGE_COACH') &&
                            sport?.id === this.context.auth.colleges?.find(Boolean)?.sportId && (
                                <Rate value={userSport?.rate} onChange={this.rate} />
                            )}
                        {user && (
                            <Select
                                className={styles.seasons}
                                size="large"
                                value={userSport?.id}
                                onChange={this.changeUserSport}
                            >
                                {userSportsOptions}
                            </Select>
                        )}
                        <Select
                            className={styles.seasons}
                            size="large"
                            value={seasonId}
                            onChange={(value) => this.refresh(value)}
                        >
                            {seasonOptions}
                        </Select>
                        {!this.context.auth && (
                            <Link to={`/signin`}>
                                <Button shape="round" className={styles.login}>
                                    <FormattedMessage id="player.data.logIn" />
                                </Button>
                            </Link>
                        )}
                        <Button
                            shape="round"
                            className={styles.share}
                            icon={<Icon component={shareSvg} />}
                            onClick={() => this.showModalShare(true)}
                        >
                            <FormattedMessage id="player.data.share" />
                        </Button>
                    </div>
                </div>
            );
        } else {
            return (
                <div className={styles.buttons}>
                    {this.context.auth?.authorities.includes('ROLE_COLLEGE_COACH') &&
                        sport?.id === this.context.auth.colleges?.find(Boolean)?.sportId && (
                            <Rate value={userSport?.rate} onChange={this.rate} />
                        )}
                    <Button
                        shape="round"
                        className={styles.share}
                        icon={<Icon component={shareSvg} />}
                        onClick={() => this.showModalShare(true)}
                    ></Button>
                    {!this.context.auth && (
                        <Link to={`/signin`}>
                            <Button shape="round" className={styles.login}>
                                <FormattedMessage id="player.data.logIn" />
                            </Button>
                        </Link>
                    )}
                </div>
            );
        }
    };

    renderRecords = (): React.ReactElement | undefined => {
        const { records, sport } = this.state;
        const recordsTypes: (string | undefined)[] = Array.from(new Set(records?.flatMap((r) => r.tab?.name)));

        return (
            records &&
            sport && (
                <div className={styles.records}>
                    <header>
                        <h2>
                            <FormattedMessage id="player.data.records" />
                        </h2>
                    </header>

                    <div className={styles.recordsData}>
                        {recordsTypes.map((rt) => [
                            <div key={rt} className={styles.label}>
                                <div>{rt}</div>
                            </div>,

                            records
                                .filter((r) => r.tab?.name === rt)
                                .map((r) => {
                                    const testCategory = sport.stations?.flatMap((s) =>
                                        s.metrics?.filter((m) => m.id === r.metricId),
                                    )[0];

                                    return (
                                        <React.Fragment key={r.metricId}>
                                            <div className={styles.record}>
                                                <Statistic
                                                    className={styles.speed}
                                                    title={
                                                        <>
                                                            <h3>{r.tab?.name}</h3>
                                                            <p>{testCategory?.name}</p>
                                                        </>
                                                    }
                                                    value={`${r.result} ${metricService.abbreviate(
                                                        testCategory?.unitOfMeasure?.code,
                                                    )}`}
                                                    precision={2}
                                                    suffix={
                                                        <div className={styles.label}>{testCategory?.description}</div>
                                                    }
                                                />
                                                {r.video!.length > 0 && (
                                                    <Button
                                                        className={styles.play}
                                                        shape={'round'}
                                                        type="primary"
                                                        icon={<Icon component={playSvg} />}
                                                        onClick={() => this.playMedia(r.video!)}
                                                    >
                                                        <FormattedMessage id="player.data.watch" />
                                                    </Button>
                                                )}
                                            </div>
                                        </React.Fragment>
                                    );
                                }),
                        ])}
                    </div>
                </div>
            )
        );
    };

    renderResults = (desktop: boolean): React.ReactElement | undefined => {
        const { records, results, sport, testCategory, editable } = this.state;

        if (records && sport && results) {
            return (
                <div className={styles.history}>
                    <h2>
                        <FormattedMessage id="player.data.history" />
                    </h2>

                    <div className={styles.data}>
                        <header>
                            <div className={styles.label}>
                                <div className={styles.logo}>
                                    <img src={process.env.PUBLIC_URL + '/img/top100-logo.png'} alt="" />
                                </div>
                                <div className={styles.txt}>
                                    <h3>
                                        <FormattedMessage id="player.data.performance" />
                                    </h3>
                                    <p>
                                        <FormattedMessage id="player.data.detail" />
                                    </p>
                                </div>
                            </div>
                            <Button
                                icon={<Icon component={addSvg} />}
                                shape="round"
                                onClick={() => this.showModalMeasure(true)}
                                hidden={!editable}
                            >
                                <FormattedMessage id="player.data.addMetric" />
                            </Button>
                        </header>

                        <div className={styles.records}>
                            <div className={styles.charts}>
                                {testCategory && this.renderCategory(results, sport, testCategory, desktop)}
                            </div>

                            <div className={styles.recommendation}>
                                <span>
                                    <FormattedMessage id="player.data.recomendation" />
                                </span>{' '}
                                <FormattedMessage id="player.data.recomendation.1" />{' '}
                                <a href="https://www.top100sports.com/training-app/" target="_blank" rel="noreferrer">
                                    <FormattedMessage id="player.data.getItHere" />
                                </a>
                            </div>

                            {this.renderBestResults()}
                        </div>
                    </div>
                </div>
            );
        }
    };

    renderCategory = (
        results: Result[],
        sport: Sport,
        category: TestCategory,
        desktop: boolean,
    ): React.ReactElement | undefined => {
        const stations = sport.stations || [];
        const categories = stations.flatMap((s) => s.metrics).filter((c) => c && this.getBestResult(results, c));
        const current = categories.filter(Boolean).findIndex((c) => c!.id === category.id);
        const previous = current === 0 ? categories.length - 1 : current - 1;
        const next = current === categories.length - 1 ? 0 : current + 1;

        return (
            <>
                <h3>
                    {category?.name}
                    <div className={styles.nav}>
                        <Button
                            icon={<Icon component={prevSvg} />}
                            ghost
                            onClick={() => this.setCategory(categories[previous])}
                        ></Button>
                        <Button
                            icon={<Icon component={nextSvg} />}
                            ghost
                            onClick={() => this.setCategory(categories[next])}
                        ></Button>
                    </div>
                </h3>
                {category && this.renderTopResult(results, category, desktop)}
                {category && this.renderLastResults(results, category, 2, desktop)}
            </>
        );
    };

    renderTopResult = (
        results: Result[],
        testCategory: TestCategory,
        desktop: boolean,
    ): React.ReactElement | undefined => {
        const topResult = this.getBestResult(results, testCategory);
        const maxResult = this.getMaxResult(results, testCategory);

        return topResult && this.renderResult(topResult, testCategory, true, maxResult, desktop);
    };

    renderLastResults = (results: Result[], testCategory: TestCategory, size: number, desktop: boolean) => {
        const maxResult = this.getMaxResult(results, testCategory);

        return results
            .filter((r) => r.metricId === testCategory.id)
            .sort((a, b) => numericService.sort(a.date, b.date))
            .slice(0, size)
            .map((r) => this.renderResult(r, testCategory, false, maxResult, desktop));
    };

    renderBestResults = (): React.ReactElement | undefined => {
        const { results, sport, testCategory } = this.state;
        const testCategories = sport?.stations?.flatMap((s) => s.metrics) || [];

        if (results && sport) {
            return (
                <div className={styles.navi}>
                    {testCategories
                        .map((tc) => tc && this.getBestResult(results, tc))
                        .filter(Boolean)
                        .filter((value, index, self) => self.indexOf(value) === index)
                        .map((result) => this.renderBestResult(sport, result!, testCategory))}
                </div>
            );
        }
    };

    renderBestResult = (sport: Sport, result: Result, category?: TestCategory): React.ReactElement | undefined => {
        if (result) {
            const stations = sport.stations || [];
            const resultCategory = stations.flatMap((s) => s.metrics?.filter((m) => m.id === result.metricId))[0];
            const unit = metricService.abbreviate(resultCategory?.unitOfMeasure?.code);

            return (
                <Button
                    size="large"
                    className={category?.id === resultCategory?.id ? styles.resultsSelected : styles.unselected}
                    onClick={() => this.setCategory(resultCategory)}
                >
                    <div className={styles.txt}>
                        {resultCategory?.name}
                        <span className={styles.value}>
                            {result.result} {unit}
                            {result.testVerified && <span className={styles.check}></span>}
                            {result.laser && <span className={styles.star}></span>}
                        </span>
                    </div>
                </Button>
            );
        } else {
            return <></>;
        }
    };

    renderResult = (
        result: Result,
        testCategory: TestCategory,
        isTop: boolean,
        maxResult?: Result,
        desktop?: boolean,
    ): React.ReactElement | undefined => {
        const { editable, loading } = this.state;
        let percent = 0;
        if (maxResult && maxResult.result && result.result) {
            percent = (result.result * 70) / maxResult.result;
        } else if (maxResult && maxResult.result) {
            percent = 0;
        } else if (result.result) {
            percent = result.result;
        }

        return (
            <div className={styles.chart}>
                <div className={styles.labels}>
                    <label>
                        {result.date && <FormattedDate value={result.date} month="2-digit" />}
                        {'.'}
                        {result.date && <FormattedDate value={result.date} day="2-digit" />}
                        {'.'}
                        {result.date && <FormattedDate value={result.date} year="numeric" />}
                    </label>
                    {isTop && (
                        <label>
                            <div className={styles.pr}>
                                <Icon component={starSvg} />
                            </div>
                            {result.testVerified && <div className={styles.check}></div>}
                            {result.laser && <div className={styles.star}></div>}
                        </label>
                    )}
                </div>

                <div className={styles.progress}>
                    {editable && (
                        <Button
                            shape={'round'}
                            size="small"
                            icon={<Icon component={editSvg} />}
                            onClick={() => this.showModalMeasure(true, result.stationId!, result.id)}
                            disabled={result.testVerified && !this.context.auth?.authorities.includes('ROLE_ADMIN')}
                            className={styles.edit}
                        ></Button>
                    )}
                    <Progress
                        strokeWidth={21}
                        className={styles.progress}
                        percent={percent}
                        format={(currentPercent) =>
                            `${result.result} ${metricService.abbreviate(testCategory.unitOfMeasure?.code)}`
                        }
                        strokeColor={isTop ? '#C41134' : '#3F8CFF'}
                    />
                    {result.video.length > 0 ? (
                        <Button
                            shape={'round'}
                            type="primary"
                            size="small"
                            icon={<Icon component={playSvg} />}
                            className={styles.play}
                            onClick={() => this.playMedia(result.video)}
                        >
                            <FormattedMessage id="player.data.watch" />
                        </Button>
                    ) : editable ? (
                        <Upload
                            beforeUpload={(value: any) => this.uploadVideo(value, result.stationId!)}
                            showUploadList={false}
                            accept=".mp4,.mpe,.mpeg,.mpg,.mpv,.mp2,.ogg,.m4p,.m4v,.avi,.wmv,.mov,.qt"
                        >
                            <Button
                                shape={'round'}
                                type="primary"
                                size="small"
                                icon={<Icon component={addSvg} />}
                                loading={loading === 'uploading'}
                                className={styles.media}
                            >
                                <FormattedMessage id="player.data.media" />
                            </Button>
                        </Upload>
                    ) : desktop ? (
                        <div className={styles.emptyItem}></div>
                    ) : (
                        <div className={styles.emptyItemMobile}></div>
                    )}
                </div>
            </div>
        );
    };

    renderButtons = (desktop: boolean): React.ReactElement | undefined => {
        return (
            <>
                {this.renderRecords()} {this.renderResults(desktop)}
            </>
        );
    };

    renderNationalRank = () => {
        const { userRating } = this.state;
        const top100Rating =
            userRating && userRating.ratings
                ? userRating.ratings.find((rating) => rating.type === 'Global')?.ratingValue
                : undefined;
        return top100Rating === 0 || !top100Rating ? <FormattedMessage id="--" /> : top100Rating;
    };

    renderAffiliateRank = () => {
        const { userRating, organization } = this.state;
        const affiliateRank =
            userRating && organization && userRating.organizationRatings && userRating.organizationRatings.length > 0
                ? userRating.organizationRatings.find((o) => o.organizationId === organization)?.rating
                : undefined;
        return affiliateRank === 0 || !affiliateRank ? <FormattedMessage id="--" /> : affiliateRank;
    };

    renderContent = (desktop: boolean): React.ReactElement | undefined => {
        const {
            user,
            userSport,
            stations,
            modalShareVisible,
            modalMeasureVisible,
            modalPlayerVisible,
            medias,
            positions,
            metricId,
            measureId,
            sport,
            organization,
            userOrganizationsBySport,
            seasonId,
        } = this.state;

        const affiliatesOptions = userOrganizationsBySport
            .filter(
                (userOrganization, index, self) =>
                    index === self.findIndex((uo) => uo.organizationId === userOrganization.organizationId),
            )
            .map((o) => (
                <Select.Option key={o.organizationId!} value={o.organizationId!}>
                    {o.organizationName}
                </Select.Option>
            ));
        return (
            <>
                <div className={styles.mobileHeaderBackground}>
                    <div className={styles.headerBackgroundGradient}></div>
                    <div className={styles.headerBackgroundImage}></div>
                </div>
                <PlayerShareModal visible={modalShareVisible} user={user} onCancel={() => this.showModalShare(false)} />
                {stations && modalMeasureVisible && (
                    <AddMetricModal
                        user={user}
                        stations={stations}
                        metricId={metricId}
                        measureId={measureId}
                        sportId={sport?.id!}
                        onCancel={() => this.showModalMeasure(false)}
                        onUpdate={this.update}
                    />
                )}
                {modalPlayerVisible && medias && medias.length > 0 && (
                    <MediaPlayerComponent onCancel={() => this.showModalPlayer(false)} medias={medias} />
                )}

                <div className={styles.player}>
                    <header>
                        <div className={styles.picture}>
                            <Image
                                src={userSport?.photoUrl || avatar}
                                fallback="data:image/png;base64,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"
                            />
                        </div>

                        <div className={styles.playerInfo}>
                            <h1>
                                {user?.profile?.givenName} {user?.profile?.familyName}
                            </h1>
                            <ul className={styles.stats}>
                                <li>
                                    <label>
                                        <FormattedMessage id="player.profile.grade" />
                                    </label>
                                    {user?.profile?.grade}
                                </li>
                                <li>
                                    <label>
                                        <FormattedMessage id="player.profile.height" />
                                    </label>
                                    {metricService.toFormattedFeets(user?.profile?.height)}
                                </li>
                                <li>
                                    <label>
                                        <FormattedMessage id="player.profile.weight" />
                                    </label>
                                    {user?.profile?.weight}{' '}
                                    <span hidden={!user?.profile?.weight}>
                                        <FormattedMessage id="player.profile.lbs" />
                                    </span>
                                </li>
                                <li>
                                    <label>
                                        <FormattedMessage id="player.profile.positions" />
                                    </label>
                                    {positions
                                        .filter((p) => userSport?.positions?.includes(p.id))
                                        .map((pa) => pa.code)
                                        .join(',')}
                                </li>
                            </ul>
                        </div>

                        <div className={styles.scores}>
                            <div className={styles.playerScore}>
                                <div>
                                    <div className={styles.score}>{this.renderNationalRank()}</div>
                                    <p>
                                        <FormattedMessage id="player.nationalRank" />
                                    </p>
                                    <img width="70" src={process.env.PUBLIC_URL + '/img/top100-logo.png'} alt="Score" />
                                </div>
                            </div>
                            <div className={styles.playerScore}>
                                <div>
                                    <div className={styles.score}>{this.renderAffiliateRank()}</div>
                                    <p>
                                        <FormattedMessage id="player.affiliateRank" />
                                    </p>
                                    <img width="70" src={process.env.PUBLIC_URL + '/img/top100-logo.png'} alt="Score" />
                                </div>
                            </div>
                        </div>

                        <div className={styles.sportSelect}>
                            <Select
                                className={styles.affiliates}
                                size="large"
                                value={organization}
                                onChange={(value) => this.changeAffiliate(value)}
                            >
                                {affiliatesOptions}
                            </Select>
                        </div>
                    </header>
                    {this.renderButtons(desktop)}
                </div>

                {user && seasonId && sport?.code !== 'LACROSSE' && (
                    <PlayerGameStatisticsComponent user={user} seasonId={seasonId} organization={organization} />
                )}
            </>
        );
    };

    render() {
        const { loading, user } = this.state;
        const playerProps = playerService.getProps(this.props, user);
        const fullName =
            user &&
            `${user?.profile?.givenName} ${user?.profile?.familyName} - ${this.props.intl.formatMessage({
                id: 'data',
            })}`;

        return (
            <>
                <HeadMetadata title={fullName} />
                <LayoutComponent
                    page="player-data"
                    content={this.renderContent}
                    header={this.renderHeader}
                    loading={loading === 'initializing'}
                    {...playerProps}
                />
            </>
        );
    }
}
export default injectIntl(PlayerDataPage);

type ParamsType = { id: string };
interface Props extends RouteComponentProps<ParamsType>, WrappedComponentProps {}

interface State {
    user?: User;
    sport?: Sport;
    userSport?: UserSport;
    userSports: UserSport[];
    sports: Sport[];
    records?: Record[];
    results?: Result[];
    stations?: Station[];
    testCategory?: TestCategory;
    medias?: Media[];
    positions: Position[];
    metricId?: number;
    measureId?: number;
    modalShareVisible?: boolean;
    modalMeasureVisible?: boolean;
    modalPlayerVisible?: boolean;
    editable?: boolean;
    file?: any;
    loading?: 'loading' | 'uploading' | 'initializing';
    seasons: Season[];
    seasonId?: number;
    userRating?: UserRating;
    userOrganizationsBySport: UserOrganizationMember[];
    organization?: string;
}
